import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";
import { PlusCircle, Pencil, Trash2, UserPlus, Users as UsersIcon, ShieldCheck, User as UserIcon, Power, PowerOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Deterministic colorful avatar palette based on username
const AVATAR_COLORS = [
  "bg-sky-100 text-sky-700",
  "bg-amber-100 text-amber-700",
  "bg-emerald-100 text-emerald-700",
  "bg-rose-100 text-rose-700",
  "bg-teal-100 text-teal-700",
  "bg-indigo-100 text-indigo-700",
  "bg-orange-100 text-orange-700",
  "bg-cyan-100 text-cyan-700",
];
function avatarClass(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i++) h = (h * 31 + name.charCodeAt(i)) % 9973;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name) {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", username: "", password: "", role: "user" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm({ name: "", username: "", password: "", role: "user" });
    setOpen(true);
  }
  function openEdit(u) {
    setEditing(u);
    setForm({ name: u.name, username: u.username, password: "", role: u.role });
    setOpen(true);
  }

  async function save() {
    try {
      if (editing) {
        const body = { name: form.name, username: form.username, role: form.role };
        if (form.password) body.password = form.password;
        await api.put(`/users/${editing.id}`, body);
        toast.success("User diperbarui");
      } else {
        if (!form.password) return toast.error("Password wajib diisi");
        await api.post("/users", form);
        toast.success("User dibuat");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function remove(u) {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User dihapus");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function toggleActive(u) {
    const next = !(u.is_active !== false);
    try {
      await api.put(`/users/${u.id}`, { is_active: next });
      toast.success(next ? `User "${u.username}" diaktifkan` : `User "${u.username}" dinonaktifkan`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <PageHeader
        overline="Administrasi"
        title="Manajemen User"
        description="Tambah, ubah, atau hapus akun pengguna sistem."
        icon={UsersIcon}
        tone="violet"
        actions={
          <Button onClick={openCreate} className="bg-brand text-white hover:bg-brand-600" data-testid="btn-create-user">
            <UserPlus className="mr-2 h-4 w-4" /> Tambah User
          </Button>
        }
      />

      <Card className="overflow-hidden border-border shadow-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Dibuat</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`row-user-${u.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className={`grid h-9 w-9 place-items-center rounded-full text-xs font-semibold ${avatarClass(u.username)}`}>
                      {initials(u.name)}
                    </div>
                    <div className="font-medium text-ink-900">{u.name}</div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm">@{u.username}</TableCell>
                <TableCell>
                  {u.role === "admin" ? (
                    <Badge className="bg-brand/10 text-brand-700 hover:bg-brand/10">
                      <ShieldCheck className="mr-1 h-3 w-3" /> admin
                    </Badge>
                  ) : u.role === "karyawan" ? (
                    <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                      <UserIcon className="mr-1 h-3 w-3" /> karyawan
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">
                      <UserIcon className="mr-1 h-3 w-3" /> user
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {u.is_active === false ? (
                    <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100" data-testid={`status-${u.id}`}>
                      <PowerOff className="mr-1 h-3 w-3" /> Non-aktif
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid={`status-${u.id}`}>
                      <Power className="mr-1 h-3 w-3" /> Aktif
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-ink-500">{formatTanggal(u.created_at)}</TableCell>
                <TableCell className="text-right">
                  {u.username !== me?.username && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => toggleActive(u)}
                      title={u.is_active === false ? "Aktifkan user" : "Nonaktifkan user"}
                      data-testid={`btn-toggle-active-${u.id}`}
                    >
                      {u.is_active === false ? <Power className="h-4 w-4 text-emerald-600" /> : <PowerOff className="h-4 w-4 text-rose-600" />}
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`btn-edit-${u.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {u.username !== me?.username && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`btn-delete-${u.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Hapus user "{u.username}"?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Tindakan ini tidak dapat dibatalkan. Data surat yang sudah dibuat tetap tersimpan.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Batal</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(u)} data-testid={`confirm-delete-${u.id}`}>Hapus</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow><TableCell colSpan={6} className="py-8 text-center text-ink-500">Belum ada user.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Ubah ${editing.username}` : "Tambah User"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Nama</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="input-user-name" />
            </div>
            <div className="space-y-1">
              <Label>Username</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="input-user-username" />
            </div>
            <div className="space-y-1">
              <Label>{editing ? "Password baru (kosongkan jika tidak diganti)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="input-user-password" />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="input-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User Surat</SelectItem>
                  <SelectItem value="karyawan">Karyawan (Absensi)</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-ink-900 text-white hover:bg-brand" data-testid="btn-save-user">
              <PlusCircle className="mr-2 h-4 w-4" /> {editing ? "Simpan" : "Buat"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
